import { TableCell } from '@carbon/react';
import { format3 } from 'utils/number';
import { fmtAmount, fmtNumber } from '../format';
import { computeLineItemVariance, formatVariancePct } from './variance';
import { formatProductionPeriod } from './line-item-utils';
import type { LineItemRow } from './types-line-item';

// Read-only line-item columns shared by the Line Item Review overview's expand
// preview AND the per-well drill-in detail (the drill-in appends Status +
// Actions). Column set + order + labels follow the mock's read-only line-item
// table. BTU is intentionally OMITTED — RC line items carry no BTU field, so an
// empty column would be noise (the mock's BTU is sample data). Kept in one place
// so the two tables can't drift.
export const READ_ONLY_LINE_ITEM_HEADERS = [
  'Product',
  'Type',
  'Adjustment Code',
  'Production Month',
  'Line Item Decimal',
  'Price',
  'Gross Volume',
  'Gross Value',
  'Gross Taxes',
  'Gross Adjustments',
  'Gross Net',
  'Owner Volume',
  'Owner Value',
  'Owner Net Value Variance %',
  'Dec Calc (Value) Variance %',
  'Dec Calc (Volume) Variance %',
  'Gross Value Variance %',
  'Gross Net Variance %',
  'Owner Taxes',
  'Owner Deducts',
  'Line Item Net Value',
];

const monthLabel = (iso: string | null) => {
  return iso ? formatProductionPeriod(iso, iso) : '—';
};

// Volumes render at 3 decimals; the line-item decimal (a fractional interest,
// e.g. 0.0009766) stays on fmtNumber so its precision isn't rounded away.
const fmtVolume = (n: number | null): string => {
  return n === null ? '--' : format3(n);
};

export const renderReadOnlyLineItemCells = (row: LineItemRow) => {
  const v = computeLineItemVariance(row);
  return (
    <>
      <TableCell>{row.product ?? '—'}</TableCell>
      <TableCell>{row.interestType ?? '—'}</TableCell>
      <TableCell>{row.adjustmentCode ?? '—'}</TableCell>
      <TableCell>{monthLabel(row.productionMonth)}</TableCell>
      <TableCell>{fmtNumber(row.lineItemDecimal)}</TableCell>
      <TableCell>{fmtAmount(row.price)}</TableCell>
      <TableCell>{fmtVolume(row.grossVolume)}</TableCell>
      <TableCell>{fmtAmount(row.grossValue)}</TableCell>
      <TableCell>{fmtAmount(row.grossTaxes)}</TableCell>
      <TableCell>{fmtAmount(row.grossAdjustments)}</TableCell>
      <TableCell>{fmtAmount(row.grossNet)}</TableCell>
      <TableCell>{fmtVolume(row.ownerVolume)}</TableCell>
      <TableCell>{fmtAmount(row.ownerValue)}</TableCell>
      <TableCell>{formatVariancePct(v.ownerNetValuePercent)}</TableCell>
      <TableCell>{formatVariancePct(v.decCalcValuePercent)}</TableCell>
      <TableCell>{formatVariancePct(v.decCalcVolumePercent)}</TableCell>
      <TableCell>{formatVariancePct(v.grossValuePercent)}</TableCell>
      <TableCell>{formatVariancePct(v.grossNetPercent)}</TableCell>
      <TableCell>{fmtAmount(row.ownerTaxes)}</TableCell>
      <TableCell>{fmtAmount(row.ownerAdjustments)}</TableCell>
      <TableCell>{fmtAmount(row.lineItemNetValue)}</TableCell>
    </>
  );
};
